import { doc, addDoc, collection, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface NotificationData {
    title: string;
    body: string;
    type: 'invoice' | 'payment' | 'status_change' | 'assignment' | 'approval' | 'complain';
    invoiceNumber?: string;
    clientName?: string;
    totalAmount?: number;
    status?: string;
    action?: 'created' | 'updated';
    /** Who triggered the last status-related update (`complain` + `updated`). */
    actor?: 'company' | 'public';
    /** Branch public complain */
    complainId?: string;
    branchLabel?: string;
    complainType?: string;
    createdAt: string;
    read: boolean;
}

export interface FCMNotificationPayload {
    to: string;
    notification: {
        title: string;
        body: string;
    };
    data?: {
        type: string;
        invoiceNumber?: string;
        clientName?: string;
        totalAmount?: string;
        status?: string;
        action?: string;
        complainId?: string;
        branchLabel?: string;
        complainType?: string;
        actor?: string;
    };
}

class NotificationService {
    /**
     * Send FCM notification to a single device
     * Uses CORS proxy for development environment
     */
    private async sendFCMNotification(payload: FCMNotificationPayload): Promise<boolean> {
        try {
            const fcmUrl = process.env.REACT_APP_FCM_ENDPOINT;

            if (!fcmUrl) {
                console.log('[FCM] REACT_APP_FCM_ENDPOINT not set; skipping mobile push.');
                return false;
            }

            const response = await fetch(fcmUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                return false;
            }

            await response.json();
            return true;
        } catch (error: any) {
            return false;
        }
    }

    /**
     * Tokens stored as docs under .../fcmTokens/{id} with fields `token`, optional `active`.
     */
    private async tokensFromFcmTokensSubcollection(...pathSegments: string[]): Promise<string[]> {
        try {
            const colRef = collection(db, ...(pathSegments as [string, ...string[]]));
            const snap = await getDocs(colRef);
            const out: string[] = [];
            snap.forEach((d) => {
                const data = d.data() as { token?: string; active?: boolean };
                if (data.active === false) {
                    return;
                }
                const t = typeof data.token === 'string' ? data.token.trim() : '';
                if (t) {
                    out.push(t);
                }
            });
            return out;
        } catch {
            return [];
        }
    }

    private mergeUniqueTokens(lists: string[][]): string[] {
        const merged = lists.flat().map((t) => t.trim()).filter(Boolean);
        const seen: { [token: string]: boolean } = {};
        const out: string[] = [];
        for (let i = 0; i < merged.length; i++) {
            const t = merged[i];
            if (!seen[t]) {
                seen[t] = true;
                out.push(t);
            }
        }
        return out;
    }

    /**
     * Get FCM tokens for a company (parent doc fields + `companies/{id}/fcmTokens` list).
     */
    private async getFCMTokens(companyId: string): Promise<string[]> {
        try {
            const fromSub = await this.tokensFromFcmTokensSubcollection('companies', companyId, 'fcmTokens');

            const companyRef = doc(db, 'companies', companyId);
            const companyDoc = await getDoc(companyRef);
            const fromDoc = companyDoc.exists()
                ? this.tokensFromDocData(companyDoc.data() as Record<string, unknown>)
                : [];

            return this.mergeUniqueTokens([fromDoc, fromSub]);
        } catch {
            return [];
        }
    }

    private tokensFromDocData(companyData: Record<string, unknown>): string[] {
        const tokens: string[] = [];
        if (companyData.fcmTokens && Array.isArray(companyData.fcmTokens)) {
            const validTokens = (companyData.fcmTokens as string[]).filter((t: string) => t && t.trim());
            tokens.push(...validTokens);
        } else if (companyData.fcmToken && typeof companyData.fcmToken === 'string') {
            tokens.push(companyData.fcmToken);
        } else if (companyData.devices && Array.isArray(companyData.devices)) {
            (companyData.devices as { token?: string }[]).forEach((device) => {
                if (device.token && typeof device.token === 'string') {
                    tokens.push(device.token);
                }
            });
        }
        return tokens.filter((t) => t && t.trim());
    }

    /**
     * Branch tokens: parent branch doc + `.../branches/{branchId}/fcmTokens` list.
     */
    private async getBranchFCMTokens(companyId: string, bankId: string, branchId: string): Promise<string[]> {
        try {
            const fromSub = await this.tokensFromFcmTokensSubcollection(
                'bankClient',
                companyId,
                'banks',
                bankId,
                'branches',
                branchId,
                'fcmTokens'
            );

            const branchRef = doc(db, 'bankClient', companyId, 'banks', bankId, 'branches', branchId);
            const branchSnap = await getDoc(branchRef);
            const fromDoc = branchSnap.exists()
                ? this.tokensFromDocData(branchSnap.data() as Record<string, unknown>)
                : [];

            return this.mergeUniqueTokens([fromDoc, fromSub]);
        } catch {
            return [];
        }
    }

    /**
     * Save notification to database
     * Structure: notifications/companies/{companyId}
     */
    private async saveNotification(
        companyId: string,
        notificationData: NotificationData
    ): Promise<void> {
        try {
            const notificationRef = collection(db, 'notifications', 'companies', companyId);
            await addDoc(notificationRef, notificationData);
        } catch (error: any) {
            // Silent fail - notification saving shouldn't break the flow
        }
    }

    private async saveBranchNotification(
        companyId: string,
        bankId: string,
        branchId: string,
        notificationData: NotificationData
    ): Promise<void> {
        try {
            const notificationRef = collection(
                db,
                'bankClient',
                companyId,
                'banks',
                bankId,
                'branches',
                branchId,
                'notifications'
            );
            await addDoc(notificationRef, notificationData);
        } catch {
            // Silent fail
        }
    }

    /** Branch portal feed + branch-held FCM tokens (same shape as company doc). */
    private async sendBranchClientNotification(
        companyId: string,
        bankId: string,
        branchId: string,
        notificationData: NotificationData
    ): Promise<boolean> {
        try {
            const fcmTokens = await this.getBranchFCMTokens(companyId, bankId, branchId);
            const endpointConfigured = Boolean(process.env.REACT_APP_FCM_ENDPOINT);

            console.log('[FCM branch]', {
                companyId,
                bankId,
                branchId,
                tokensFound: fcmTokens.length,
                endpointConfigured,
            });

            if (fcmTokens.length === 0) {
                console.log('[FCM branch] No branch device tokens; saving notification for branch web feed only.');
                await this.saveBranchNotification(companyId, bankId, branchId, notificationData);
                return false;
            }

            await this.saveBranchNotification(companyId, bankId, branchId, notificationData);

            let successCount = 0;
            for (let i = 0; i < fcmTokens.length; i++) {
                const token = fcmTokens[i];
                const payload: FCMNotificationPayload = {
                    to: token,
                    notification: {
                        title: notificationData.title,
                        body: notificationData.body,
                    },
                    data: {
                        type: notificationData.type,
                        invoiceNumber: notificationData.invoiceNumber || '',
                        clientName: notificationData.clientName || '',
                        totalAmount: notificationData.totalAmount?.toString() || '',
                        status: notificationData.status || '',
                        action: notificationData.action || '',
                        complainId: notificationData.complainId || '',
                        branchLabel: notificationData.branchLabel || '',
                        complainType: notificationData.complainType || '',
                        actor: notificationData.actor || '',
                    },
                };

                const fcmResult = await this.sendFCMNotification(payload);
                console.log(
                    '[FCM branch] Token',
                    i + 1,
                    '/',
                    fcmTokens.length,
                    fcmResult ? 'sent to mobile OK' : 'send failed or endpoint missing'
                );
                if (fcmResult) {
                    successCount++;
                }
            }

            console.log('[FCM branch] Summary: delivered to mobile', successCount, '/', fcmTokens.length);

            return successCount > 0;
        } catch {
            try {
                await this.saveBranchNotification(companyId, bankId, branchId, {
                    ...notificationData,
                    createdAt: new Date().toISOString(),
                });
            } catch {
                // ignore
            }
            return false;
        }
    }

    /**
     * Send notification to company
     */
    async sendCompanyNotification(
        companyId: string,
        notificationData: NotificationData
    ): Promise<boolean> {
        try {
            // Get company FCM tokens
            const fcmTokens = await this.getFCMTokens(companyId);
            const endpointConfigured = Boolean(process.env.REACT_APP_FCM_ENDPOINT);

            console.log('[FCM]', {
                companyId,
                tokensFound: fcmTokens.length,
                endpointConfigured,
            });

            if (fcmTokens.length === 0) {
                console.log('[FCM] No device tokens on company document; saving notification for web only.');
                await this.saveNotification(companyId, notificationData);
                return false;
            }

            // Save notification to database
            await this.saveNotification(companyId, notificationData);

            // Send FCM notification to all tokens
            let successCount = 0;
            for (let i = 0; i < fcmTokens.length; i++) {
                const token = fcmTokens[i];
                const payload: FCMNotificationPayload = {
                    to: token,
                    notification: {
                        title: notificationData.title,
                        body: notificationData.body,
                    },
                    data: {
                        type: notificationData.type,
                        invoiceNumber: notificationData.invoiceNumber || '',
                        clientName: notificationData.clientName || '',
                        totalAmount: notificationData.totalAmount?.toString() || '',
                        status: notificationData.status || '',
                        action: notificationData.action || '',
                        complainId: notificationData.complainId || '',
                        branchLabel: notificationData.branchLabel || '',
                        complainType: notificationData.complainType || '',
                        actor: notificationData.actor || '',
                    },
                };

                const fcmResult = await this.sendFCMNotification(payload);
                console.log('[FCM] Token', i + 1, '/', fcmTokens.length, fcmResult ? 'sent to mobile OK' : 'send failed or endpoint missing');
                if (fcmResult) {
                    successCount++;
                }
            }

            console.log('[FCM] Summary: delivered to mobile', successCount, '/', fcmTokens.length);

            if (successCount > 0) {
                return true;
            } else {
                return false;
            }
        } catch (error: any) {
            try {
                await this.saveNotification(companyId, {
                    ...notificationData,
                    createdAt: new Date().toISOString(),
                });
            } catch (saveError) {
                // Silent fail
            }
            return false;
        }
    }

    /**
     * Send invoice notification to company
     */
    async sendInvoiceNotification(
        companyId: string,
        invoiceNumber: string,
        action: 'created' | 'updated',
        invoiceData?: {
            clientName?: string;
            totalAmount?: number;
            status?: string;
        }
    ): Promise<boolean> {
        try {
            const title = action === 'created'
                ? 'New Invoice Created'
                : 'Invoice Updated';

            const body = action === 'created'
                ? `Invoice ${invoiceNumber} has been created${invoiceData?.clientName ? ` for ${invoiceData.clientName}` : ''}`
                : `Invoice ${invoiceNumber} has been updated${invoiceData?.status ? ` - Status: ${invoiceData.status}` : ''}`;

            const notificationData: NotificationData = {
                title,
                body,
                type: 'invoice',
                invoiceNumber,
                clientName: invoiceData?.clientName,
                totalAmount: invoiceData?.totalAmount,
                status: invoiceData?.status,
                action,
                createdAt: new Date().toISOString(),
                read: false,
            };

            const result = await this.sendCompanyNotification(companyId, notificationData);
            return result;
        } catch (error: any) {
            return false;
        }
    }

    /**
     * Send payment notification to company
     */
    async sendPaymentNotification(
        companyId: string,
        invoiceNumber: string,
        amount: number,
        clientName?: string
    ): Promise<boolean> {
        const notificationData: NotificationData = {
            title: 'Payment Received',
            body: `Payment of ${amount} received for invoice ${invoiceNumber}${clientName ? ` from ${clientName}` : ''}`,
            type: 'payment',
            invoiceNumber,
            clientName,
            totalAmount: amount,
            createdAt: new Date().toISOString(),
            read: false,
        };

        return await this.sendCompanyNotification(companyId, notificationData);
    }

    /**
     * Send test notification to company
     */
    async sendTestNotification(
        companyId: string,
        message?: string
    ): Promise<boolean> {
        const notificationData: NotificationData = {
            title: 'Test Notification',
            body: message || `This is a test notification. FCM is working correctly!`,
            type: 'status_change',
            status: 'test',
            createdAt: new Date().toISOString(),
            read: false,
        };

        return await this.sendCompanyNotification(companyId, notificationData);
    }

    /** New branch complain (public portal); persists for company feed + FCM when configured. */
    async sendComplainCreatedNotification(
        companyId: string,
        params: {
            complainId: string;
            complainType: string;
            branchLabel: string;
            descriptionPreview?: string;
        }
    ): Promise<boolean> {
        const { complainId, complainType, branchLabel, descriptionPreview } = params;
        const preview =
            descriptionPreview && descriptionPreview.length > 120
                ? `${descriptionPreview.slice(0, 117)}…`
                : descriptionPreview || '';
        const body = `${branchLabel}: ${complainType}${preview ? ` — ${preview}` : ''}`;

        const notificationData: NotificationData = {
            title: 'New branch complain',
            body,
            type: 'complain',
            complainId,
            branchLabel,
            complainType,
            action: 'created',
            actor: 'public',
            createdAt: new Date().toISOString(),
            read: false,
        };

        return this.sendCompanyNotification(companyId, notificationData);
    }

    /** After complain status changes: company inbox (+FCM); branch inbox (+FCM) when company staff updated status. */
    async sendComplainStatusChangeNotification(params: {
        companyId: string;
        bankId: string;
        branchId: string;
        complainId: string;
        previousStatus: string;
        newStatus: string;
        complainType: string;
        branchLabel: string;
        actor: 'company' | 'public';
    }): Promise<void> {
        const humanize = (s: string) =>
            String(s)
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase());
        const title = 'Complain status updated';
        const body = `${params.branchLabel} — ${params.complainType} (${params.complainId}): ${humanize(
            params.previousStatus
        )} → ${humanize(params.newStatus)}`;
        const ts = new Date().toISOString();
        const notificationData: NotificationData = {
            title,
            body,
            type: 'complain',
            complainId: params.complainId,
            branchLabel: params.branchLabel,
            complainType: params.complainType,
            status: params.newStatus,
            action: 'updated',
            actor: params.actor,
            createdAt: ts,
            read: false,
        };

        const tasks: Promise<unknown>[] = [this.sendCompanyNotification(params.companyId, notificationData)];
        if (params.actor === 'company') {
            tasks.push(
                this.sendBranchClientNotification(
                    params.companyId,
                    params.bankId,
                    params.branchId,
                    notificationData
                )
            );
        }
        await Promise.all(tasks);
    }
}

export const notificationService = new NotificationService();

// Export convenience functions for backward compatibility
export const sendFCMNotification = async (
    companyId: string,
    title: string,
    body: string,
    data?: Record<string, any>
): Promise<void> => {
    const notificationData: NotificationData = {
        title,
        body,
        type: data?.type || 'status_change',
        invoiceNumber: data?.invoiceNumber,
        clientName: data?.clientName,
        totalAmount: data?.totalAmount,
        status: data?.status,
        action: data?.action,
        createdAt: new Date().toISOString(),
        read: false,
    };

    await notificationService.sendCompanyNotification(companyId, notificationData);
};

export const sendInvoiceNotification = async (
    companyId: string,
    invoiceNumber: string,
    action: 'created' | 'updated',
    invoiceData?: {
        clientName?: string;
        totalAmount?: number;
        status?: string;
    }
): Promise<void> => {
    await notificationService.sendInvoiceNotification(companyId, invoiceNumber, action, invoiceData);
};
